/**
 * useValidateUserDetailsState()
 *
 * Validates userDetails state is complete
 * If not, set error message and navigate to previous step
 *
 * Used by steps of donate journeys that expect a complete userDetails state
 * ...to prevent them being accessed in the wrong order (e.g. browser forward/back or copying/pasting an old URL)
 */
import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import { setErrorMessage } from '@/features/paymentProcess/transactionSlice';

import { UserDetailsState } from './userDetailsSlice';

export const validateState = (
  userDetailsState: UserDetailsState
): boolean => {
  const userDetailsKeys: Partial<keyof UserDetailsState>[] = [
    'firstName',
    'lastName',
    'email',
    'address1',
    'town',
    'postcode',
    'country',
  ];
  return userDetailsKeys.every(key => (userDetailsState[key]));
};

export function useValidateUserDetailsState() {
  // Takes value from state once (on load), do not re-render on state change (using `() => true` equality check as second argument)
  const userDetailsState = useAppSelector(state => state.userDetails, () => true);
  const dispatch = useAppDispatch();

  const journeyNavigate = useJourneyNavigate();

  useEffect(() => {
    // If transaction is complete (as expected), do nothing
    if (validateState(userDetailsState)) {
      return;
    }

    dispatch(setErrorMessage('You must complete this step before you can proceed.'));
    journeyNavigate('back');
  }, [userDetailsState, dispatch, journeyNavigate]);
}
